import type { ApiCalendarEntry, ApiDate, ApiTime } from "./types";

// Types expected by the <lms-calendar> web component
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

export interface CalendarTime {
  hour: number;
  minute: number;
}

export interface CalendarTimeInterval {
  start: CalendarTime;
  end: CalendarTime;
}

export interface CalendarDateInterval {
  start: CalendarDate;
  end: CalendarDate;
}

export interface CalendarEntry {
  date: CalendarDateInterval;
  time: CalendarTimeInterval;
  heading: string;
  content: string;
  color: string;
  isContinuation: boolean;
}

/**
 * Convert an API event to a CalendarEntry for the <lms-calendar> component.
 * No ISO conversion needed — the API already uses decomposed dates.
 */
export function eventToEntry(event: ApiCalendarEntry): CalendarEntry {
  return {
    date: { start: event.date.start, end: event.date.end },
    time: event.time
      ? { start: event.time.start, end: event.time.end }
      : { start: { hour: 0, minute: 0 }, end: { hour: 23, minute: 59 } },
    heading: event.heading,
    content: event.content ?? "",
    color: event.color,
    isContinuation: false,
  };
}

/**
 * Format YYYY-MM-DD string from an ApiDate.
 */
export function apiDateToString(d: ApiDate): string {
  return `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
}

/**
 * Get the date range for fetching events based on the current view.
 * Returns YYYY-MM-DD strings (the format kalendus-server expects).
 */
export function getDateRangeForView(
  date: CalendarDate,
  view: string,
): { start: string; end: string } {
  switch (view) {
    case "day": {
      const d = apiDateToString(date);
      return { start: d, end: d };
    }
    case "week": {
      const jsDate = new Date(date.year, date.month - 1, date.day);
      const dayOfWeek = jsDate.getDay();
      const start = new Date(jsDate);
      start.setDate(start.getDate() - dayOfWeek);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return {
        start: formatJsDate(start),
        end: formatJsDate(end),
      };
    }
    case "year": {
      return {
        start: `${date.year}-01-01`,
        end: `${date.year}-12-31`,
      };
    }
    case "month":
    default: {
      // Generous range: previous month through next month
      const start = new Date(date.year, date.month - 2, 1);
      const end = new Date(date.year, date.month + 1, 0);
      return {
        start: formatJsDate(start),
        end: formatJsDate(end),
      };
    }
  }
}

function formatJsDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Format a time display string from an ApiTime.
 */
export function formatTime(t: ApiTime): string {
  return `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
}

/**
 * Parse a YYYY-MM-DD <input type="date"> value into an ApiDate.
 */
export function inputValueToApiDate(value: string): ApiDate {
  const [y, m, d] = value.split("-").map(Number);
  return { year: y!, month: m!, day: d! };
}

/**
 * Parse a HH:MM <input type="time"> value into an ApiTime.
 */
export function inputValueToApiTime(value: string): ApiTime {
  const [h, m] = value.split(":").map(Number);
  return { hour: h!, minute: m! };
}

/**
 * Format a date range for display.
 */
export function formatDateRange(event: ApiCalendarEntry): string {
  const startDate = new Date(
    event.date.start.year,
    event.date.start.month - 1,
    event.date.start.day,
  );
  const dateStr = startDate.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  if (!event.time) {
    return dateStr;
  }
  return `${dateStr}, ${formatTime(event.time.start)} - ${formatTime(event.time.end)}`;
}
